import { auth } from "./firebase";
import { state } from "./state";
import { trackEvent } from "./analytics";
import type { ApiErrorBody } from "../types/api";

function resolveLocalApiBase(): string {
  const hostname = window.location.hostname;
  const isPrivateLanIp =
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://127.0.0.1:5001/aiclotheschanger-19ecf/us-central1/api";
  }

  if (isPrivateLanIp) {
    return `http://${hostname}:5001/aiclotheschanger-19ecf/us-central1/api`;
  }

  return "";
}

export const API_BASE = resolveLocalApiBase();

/**
 * Get a fresh Firebase ID token, refreshing if necessary
 */
async function getFreshToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn("No authenticated user found");
      return null;
    }

    // Get fresh token (Firebase will handle caching and refresh automatically)
    const token = await user.getIdToken(true); // true forces refresh
    
    // Update state with fresh token
    state.idToken = token;
    
    return token;
  } catch (error) {
    console.error("Failed to get fresh Firebase token:", error);
    return null;
  }
}

/**
 * Enhanced API request with automatic token refresh and better error handling
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers || {});
  
  // Always try to get a fresh token for authenticated requests
  let token = state.idToken;
  if (auth.currentUser && (!token || token === "null")) {
    console.log("Getting fresh token for API request");
    token = await getFreshToken();
  }
  
  // Add authentication header if we have a token
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
    console.log(`API Request with auth: ${path}`);
  } else {
    console.log(`API Request without auth: ${path}`);
  }
  
  // Set default content type for requests with body
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  
  // Always include platform header
  headers.set("X-App-Platform", "Web");

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const requestUrl = `${API_BASE}${normalizedPath}`;
  
  console.log(`Making API request: ${options.method || 'GET'} ${requestUrl}`, {
    hasAuth: !!token,
    tokenLength: token?.length || 0,
    headers: Object.fromEntries(headers.entries()),
  });
  
  const response = await fetch(requestUrl, { ...options, headers });
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = (isJson ? await response.json() : null) as (T & ApiErrorBody) | null;

  if (!response.ok) {
    const errBody = data as ApiErrorBody | null;
    const detailMessage = errBody?.details?.[0]?.message;
    const message =
      detailMessage ||
        errBody?.error ||
        errBody?.message ||
        `Request failed with ${response.status}`;

    console.error(`API request failed: ${response.status}`, {
      url: requestUrl,
      error: message,
      errorBody: errBody,
      hasAuth: !!token,
    });

    trackEvent("api_error", {
      path: normalizedPath,
      status: response.status,
      error_code: errBody?.code,
      message,
    });

    throw new Error(message);
  }

  console.log(`API request successful: ${response.status} ${requestUrl}`);
  return data as T;
}

/**
 * Make an authenticated API request (throws if user is not authenticated)
 */
export async function authenticatedApiRequest<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!auth.currentUser) {
    throw new Error("User must be authenticated to make this request");
  }
  
  return apiRequest<T>(path, options);
}

/**
 * Make a public API request (no authentication required)
 */
export async function publicApiRequest<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // Create new headers without authorization
  const headers = new Headers(options.headers || {});
  headers.delete("Authorization"); // Make sure no auth header is included
  
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-App-Platform", "Web");

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const requestUrl = `${API_BASE}${normalizedPath}`;
  
  const response = await fetch(requestUrl, { ...options, headers });
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = (isJson ? await response.json() : null) as (T & ApiErrorBody) | null;

  if (!response.ok) {
    const errBody = data as ApiErrorBody | null;
    const detailMessage = errBody?.details?.[0]?.message;
    const message =
      detailMessage ||
        errBody?.error ||
        errBody?.message ||
        `Request failed with ${response.status}`;
    console.warn(`Public API request failed: ${response.status} ${requestUrl}`, message);
    trackEvent("api_error", {
      path: normalizedPath,
      status: response.status,
      error_code: errBody?.code,
      message,
    });
    throw new Error(message);
  }

  return data as T;
}
